/**
 * Audits summary band (SDS KPI ④) — the compliance posture that sits above the
 * Audits list. Four hairline-separated stats derived from the program's audit
 * record (and the in-progress audit's corrective actions): the current live
 * score, open corrective actions, the next audit date, and how many audits are
 * published. A PM reads compliance standing without opening a single audit.
 *
 * Projection-gated (no_view → nothing) but NOT field-masked: audit posture is
 * operational/compliance, shown to every viewer who can see the program.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { cn } from '../../../lib/utils';
import { currentPeriodMonth, deriveAuditStatus } from '../../../pages/Delivery/Compliance/auditWorkflow';
import { correctiveActionHooks, useAudits } from '../../../pages/Delivery/Compliance/hooks';
const TONE_CLASS = {
    danger: 'text-danger',
    warning: 'text-warning',
    success: 'text-success',
    foreground: 'text-foreground',
    muted: 'text-muted-foreground'
};
// Corrective actions in these states are settled — everything else is "open".
const TERMINAL_CA_STATES = new Set([
    'Verified',
    'Cancelled',
    'Closed'
]);
// "2026-07" → "July 2026" (long), or the current month when the audit carries
// no period_month (legacy rows).
function longMonthYear(periodMonth) {
    const [y, m] = periodMonth.split('-').map(Number);
    if (!y || !m) return periodMonth;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
    });
}
function longMonth(periodMonth) {
    const [y, m] = periodMonth.split('-').map(Number);
    if (!y || !m) return periodMonth;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
        month: 'long',
        timeZone: 'UTC'
    });
}
function fmtDay(d) {
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    });
}
// "due Aug 4 – 11" when the open corrective actions share a month, else a full
// range; a single date collapses to "due Aug 4".
function fmtDueRange(dates) {
    if (dates.length === 0) return 'none open';
    const sorted = [
        ...dates
    ].sort((a, b)=>a.getTime() - b.getTime());
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (first.getTime() === last.getTime()) return `due ${fmtDay(first)}`;
    if (first.getUTCFullYear() === last.getUTCFullYear() && first.getUTCMonth() === last.getUTCMonth()) {
        return `due ${fmtDay(first)} – ${last.getUTCDate()}`;
    }
    return `due ${fmtDay(first)} – ${fmtDay(last)}`;
}
export function AuditList({ programId }) {
    const view = useView(programRef(programId));
    // One hook, one queryFn, one shape for the shared ['audit','list',programId]
    // key — the Audits page reads the same cache entry.
    const { data, isLoading, isError } = useAudits(programId);
    // data is { items } (shared key with the Audits page hook); tolerate a bare
    // array too so a stale cache entry can never crash the widget.
    const audits = Array.isArray(data) ? data : data?.items ?? [];
    const period = currentPeriodMonth();
    // The live audit — non-final, still open. Its score is the current standing
    // and its corrective actions are the open ones.
    const current = audits.find((a)=>!a.isFinal && deriveAuditStatus(a).label === 'In Progress');
    // Corrective actions for the in-progress audit only (published/final audits
    // are closed, so their actions are settled). One targeted query — the open-CA
    // count is not carried on the audit list or the financial summary.
    const caQuery = correctiveActionHooks.useList({
        auditId: current?.id ?? ''
    }, {
        enabled: !!current?.id
    });
    const openCas = (caQuery.data?.items ?? []).filter((ca)=>!TERMINAL_CA_STATES.has(ca.state));
    const openDueDates = openCas.map((ca)=>ca.dueDate ? ca.dueDate.toDate() : null).filter((d)=>d != null);
    if (view.viewMode === 'no_view') return null;
    if (isError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-lg border bg-card p-4",
            "data-testid": Testids.deliveryCompliance.auditList,
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-destructive",
                children: "Couldn't load audits."
            })
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-lg border bg-card p-4",
            "data-testid": Testids.deliveryCompliance.auditList,
            children: /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-20 w-full"
            })
        });
    }
    // CURRENT SCORE — the live pass/total, red until it clears.
    const hasScore = !!current && current.totalQuestions > 0;
    const perfect = hasScore && current.passCount === current.totalQuestions;
    // NEXT AUDIT — publishes on the first of next month and evaluates this month.
    const [py, pm] = period.split('-').map(Number);
    const nextAuditDate = new Date(Date.UTC(py, pm, 1));
    // PUBLISHED — audits frozen into the record; note whether they're KPI-exempt.
    const published = audits.filter((a)=>deriveAuditStatus(a).label === 'Complete');
    const allFinal = published.length > 0 && published.every((a)=>a.isFinal);
    const stats = [
        {
            name: 'Current Score',
            value: hasScore ? `${current.passCount} / ${current.totalQuestions}` : '—',
            tone: !hasScore ? 'muted' : perfect ? 'success' : 'danger',
            note: current ? `${longMonth(current.periodMonth || period)} · in progress` : 'no audit in progress'
        },
        {
            name: 'Open Corrections',
            value: current && caQuery.isLoading ? '—' : String(openCas.length),
            tone: openCas.length > 0 ? 'warning' : 'foreground',
            note: fmtDueRange(openDueDates)
        },
        {
            name: 'Next Audit',
            value: fmtDay(nextAuditDate),
            tone: 'foreground',
            note: `evaluates ${longMonthYear(period)}`
        },
        {
            name: 'Published',
            value: String(published.length),
            tone: 'foreground',
            note: allFinal ? 'final · KPI exempt' : 'in the KPI record'
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border bg-card px-[22px] py-[18px]",
        "data-testid": Testids.deliveryCompliance.auditList,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-2 gap-y-4 sm:grid-cols-4 sm:gap-y-0",
                children: stats.map((s, i)=>/*#__PURE__*/ _jsxs("div", {
                        className: cn('sm:mr-[18px] sm:border-r sm:border-border sm:pr-[18px]', // The rightmost column carries no trailing hairline.
                        i === stats.length - 1 && 'sm:mr-0 sm:border-r-0 sm:pr-0'),
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.07em] text-muted-foreground",
                                children: s.name
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn('mt-1.5 font-display text-[24px] font-normal leading-none tabular-nums', TONE_CLASS[s.tone]),
                                children: s.value
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-[5px] text-[11px] text-muted-foreground",
                                children: s.note
                            })
                        ]
                    }, s.name))
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "mt-3.5 text-[11.5px] leading-relaxed text-muted-foreground",
                children: [
                    "Monthly cadence — each audit evaluates the prior month. The ",
                    longMonth(period),
                    " audit publishes into the SDS Compliance KPI on ",
                    fmtDay(nextAuditDate),
                    "."
                ]
            })
        ]
    });
}
